// src/infrastructure/api/mappers/clinicalExaminationMapper.ts
import type {
  SensoryFormData,
  NeurologicalHistoryFormData,
  BehavioralFormData,
  CognitiveFunctionalFormData,
} from "../../../presentation/pages/ClinicalExamination/types";
import type {
  AccessibilitySensoryDTO,
  CaregiverQuestionnaireDTO,
  HearingFunctionalPart,
  PatientAssessmentDetailDTO,
  SpeechFunctionalPart,
} from "../../../domain/dto/ClinicalExaminationDTO";
import type { PatientAssessmentDetail } from "@/features/patients/domain/entities/Patient";

export interface CaregiverQuestionnaireSkippedSteps {
  neurological?: boolean;
  behavioral?: boolean;
  cognitiveFunctional?: boolean;
}

export const mapSkippedSensoryStepToAccessibilitySensoryDTO = () => ({
  vision: {
    severity: null,
    functional_parts: null,
    metadata: {
      visual_aid_purpose: null,
      other_text: null,
    },
  },
  hearing: {
    severity: null,
    functional_parts: null,
  },
  speech: {
    severity: null,
    functional_parts: null,
  },
});

const skippedNeurologicalMedicalHistory = () => ({
  prior_diagnosis: {
    has_diagnosis: null,
    condition: null,
    year_of_diagnosis: null,
  },
  stroke_history: {
    history_of_stroke: null,
    stroke_years: null,
    history_of_tia: null,
    tia_years: null,
    multiple_strokes: null,
    multiple_strokes_years: null,
  },
  head_injury: {
    significant_head_injury: null,
    loss_of_consciousness: null,
    year: null,
  },
  cardiovascular_risk_factors: {
    hypertension: null,
    diabetes: null,
    high_cholesterol: null,
    heart_disease: null,
  },
  family_history: {
    has_family_history: null,
    relation: null,
    other_relation: null,
  },
  onset_of_symptoms: {
    gradual_progression: null,
    sudden_onset: null,
    step_wise_decline: null,
  },
});

const skippedBehavioralPsychologicalChanges = () => ({
  sleep_past_7_days: null,
  mood_past_2_weeks: null,
  anxiety_current: null,
  interest_in_activities_past_week: null,
});

const skippedCognitiveFunctionalIndicators = () => ({
  memory: {
    forgets_recent_events: null,
    repeats_questions: null,
  },
  executive_function: {
    difficulty_planning_tasks: null,
    difficulty_managing_finances: null,
  },
  language: {
    difficulty_finding_words: null,
    difficulty_understanding_speech: null,
  },
  visuospatial: {
    gets_lost_in_familiar_places: null,
    difficulty_recognizing_faces_or_objects: null,
  },
  perception_sleep: {
    visual_hallucinations: null,
    acts_out_dreams: null,
  },
  motor_symptoms: {
    tremors_or_stiffness: null,
    slowed_movement_or_balance_issues: null,
  },
});

/**
 * Map SensoryFormData to AccessibilitySensoryDTO
 */
// Updated mapSeverity function
const mapSeverity = (
  status: string,
  type: "vision" | "hearing" | "speech" = "vision",
): SeverityLevel => {
  const upperStatus = status?.toUpperCase() || "";

  // Map for hearing - requires _LOSS suffix
  if (type === "hearing") {
    switch (upperStatus) {
      case "NORMAL":
        return "NORMAL";
      case "MILD":
        return "MILD_LOSS";
      case "MODERATE":
        return "MODERATE_LOSS";
      case "SEVERE":
        return "SEVERE_LOSS";
      case "PROFOUND":
        return "PROFOUND_LOSS";
      case "DEAF":
        return "DEAF";
      default:
        return "NORMAL";
    }
  }

  // Speech severity (different enum from vision)
  if (type === "speech") {
    switch (upperStatus) {
      case "NORMAL":
      case "NO_ISSUES":
      case "GOOD":
      case "CLEAR":
        return "NORMAL";
      case "MILD":
      case "MILD_MODERATE":
        return "MILD_DIFFICULTY";
      case "MODERATE":
        return "MODERATE_IMPAIRMENT";
      case "SEVERE":
        return "SEVERE_IMPAIRMENT";
      case "NON-SPEAKING":
      case "NON_SPEAKING":
        return "NON_VERBAL";
      default:
        return "NORMAL";
    }
  }

  // Vision severity
  switch (upperStatus) {
    case "NORMAL":
    case "NO_ISSUES":
    case "GOOD":
    case "CLEAR":
      return "NORMAL";
    case "MILD":
    case "MODERATE":
    case "MILD_MODERATE":
      return "MILD_MODERATE";
    case "SEVERE":
      return "SEVERE";
    default:
      return "NORMAL";
  }
};

/**
 * Map vision form data to functional parts array
 */
const mapVisionFunctionalParts = (
  formData: SensoryFormData,
): VisionFunctionalPart[] => {
  const parts: VisionFunctionalPart[] = [];

  if (formData.visionAssistive?.wearsGlasses) {
    parts.push("WEARS_GLASSES");
  }
  if (formData.visionAssistive?.usesContacts) {
    parts.push("USES_CONTACT_LENSES"); // ✅ Fixed: was "USES_CONTACTS"
  }
  if (formData.visionAssistive?.sensitiveToLight) {
    parts.push("SENSITIVE_TO_LIGHT");
  }

  return parts;
};
/**
 * Map hearing form data to functional parts array
 */
const mapHearingFunctionalParts = (
  formData: SensoryFormData,
): HearingFunctionalPart[] => {
  const parts: HearingFunctionalPart[] = [];

  if (formData.hearingAssistive?.usesHearingAid) {
    parts.push("USES_HEARING_AID");
  }
  if (formData.hearingAssistive?.hasCochlearImplant) {
    parts.push("HAS_COCHLEAR_IMPLANT");
  }
  if (formData.hearingAssistive?.usesSignLanguage) {
    parts.push("USES_SIGN_LANGUAGE_OR_WRITTEN"); // ✅ Fixed: was "USES_SIGN_LANGUAGE"
  }

  return parts;
};

/**
 * Map speech form data to functional parts array
 */
const mapSpeechFunctionalParts = (
  formData: SensoryFormData,
): SpeechFunctionalPart[] => {
  const parts: SpeechFunctionalPart[] = [];

  if (formData.communicationMethods?.speaksIndependently) {
    parts.push("SPEAKS_INDEPENDENTLY");
  }
  if (formData.communicationMethods?.usesAACDevice) {
    parts.push("USES_COMMUNICATION_BOARD_AAC");
  }
  if (formData.communicationMethods?.usesGestures) {
    parts.push("USES_GESTURES_CAREGIVER_ASSISTANCE");
  }

  return parts;
};

const mapVisualAidPurpose = (formData: SensoryFormData): string[] => {
  const purposes: string[] = [];

  if (formData.visualAidPurpose?.forReading) {
    purposes.push("FOR_READING");
  }
  if (formData.visualAidPurpose?.forDistance) {
    purposes.push("FOR_DISTANCE");
  }
  if (formData.visualAidPurpose?.other) {
    purposes.push("OTHER");
  }

  return purposes;
};

/**
 * Map SensoryFormData to AccessibilitySensoryDTO (new format)
 */
export const mapSensoryFormDataToAccessibilitySensoryDTO = (
  formData: SensoryFormData,
): AccessibilitySensoryDTO => {
  return {
    vision: {
      severity: mapSeverity(formData.visionStatus, "vision"),
      functional_parts: mapVisionFunctionalParts(formData),

      metadata: {
        visual_aid_purpose: mapVisualAidPurpose(formData),
        other_text: formData.visualAidOtherText || "",
      },
    },

    hearing: {
      severity: mapSeverity(formData.hearingStatus, "hearing"),
      functional_parts: mapHearingFunctionalParts(formData),
    },

    speech: {
      severity: mapSeverity(formData.speechStatus, "speech"),
      functional_parts: mapSpeechFunctionalParts(formData),
    },
  };
};
/**
 * Map NeurologicalHistoryFormData to neurological_medical_history
 */
// export const mapNeurologicalToNeurologicalMedicalHistory = (
//   formData: NeurologicalHistoryFormData,
// ): CaregiverQuestionnaireDTO["neurological_medical_history"] => {
//   return {
//     has_neurological_diagnosis: formData.hasNeurologicalDiagnosis,
//     has_had_stroke: formData.hasHadStroke,
//     has_had_tia: formData.hasHadTIA,
//     has_multiple_strokes: formData.hasMultipleStrokes,
//     has_head_injury: formData.hasHeadInjury,
//     has_hypertension: formData.hasHypertension,
//     has_diabetes: formData.hasDiabetes,
//     has_high_cholesterol: formData.hasHighCholesterol,
//     has_heart_disease: formData.hasHeartDisease,
//     has_family_neurodegenerative: formData.hasFamilyNeurodegenerative,
//     onset_pattern: formData.onsetPattern,
//   };
// };

// Update the mapNeurologicalToNeurologicalMedicalHistory function
export const mapNeurologicalToNeurologicalMedicalHistory = (
  formData: NeurologicalHistoryFormData,
): CaregiverQuestionnaireDTO["neurological_medical_history"] => {
  return {
    prior_diagnosis: {
      has_diagnosis: formData.hasNeurologicalDiagnosis ?? false,
      condition: formData.neurologicalCondition || "",
      year_of_diagnosis: formData.neurologicalDiagnosisYear || "",
    },

    stroke_history: {
      history_of_stroke: formData.hasHadStroke ?? false,
      stroke_years: formData.strokeYear || "",
      history_of_tia: formData.hasHadTIA ?? false,
      tia_years: formData.tiaYear || "",
      multiple_strokes: formData.hasMultipleStrokes ?? false,
      multiple_strokes_years: formData.multipleStrokesYear || "",
    },

    head_injury: {
      significant_head_injury: formData.hasHeadInjury ?? false,
      loss_of_consciousness: formData.headInjuryLossOfConsciousness ?? false,
      year: formData.headInjuryYear || "",
    },

    cardiovascular_risk_factors: {
      hypertension: formData.hasHypertension ?? false,
      hypertension_year: formData.hypertensionYear || "",
      diabetes: formData.hasDiabetes ?? false,
      diabetes_year: formData.diabetesYear || "",
      high_cholesterol: formData.hasHighCholesterol ?? false,
      high_cholesterol_year: formData.highCholesterolYear || "",
      heart_disease: formData.hasHeartDisease ?? false,
      heart_disease_year: formData.heartDiseaseYear || "",
    },

    family_history: {
      has_family_history: formData.hasFamilyNeurodegenerative ?? false,
      relation: formData.familyRelation || "PARENT",
      other_relation: formData.familyOtherRelation || "",
    },

    onset_of_symptoms: {
      gradual_progression: formData.onsetPattern === "gradual",
      sudden_onset: formData.onsetPattern === "sudden",
      step_wise_decline: formData.onsetPattern === "stepwise",
    },
  };
};
/**
 * Map BehavioralFormData to behavioral_psychological_changes
 */
export const mapBehavioralToBehavioralPsychologicalChanges = (
  formData: BehavioralFormData,
): CaregiverQuestionnaireDTO["behavioral_psychological_changes"] => {
  return {
    sleep_quality: formData.sleepQuality,
    depression_frequency: formData.depressionFrequency,
    anxiety_level: formData.anxietyLevel,
    apathy_level: formData.apathyLevel,
  };
};

/**
 * Map CognitiveFunctionalFormData to cognitive_functional_indicators
 */
export const mapCognitiveFunctionalToCognitiveFunctionalIndicators = (
  formData: CognitiveFunctionalFormData,
): CaregiverQuestionnaireDTO["cognitive_functional_indicators"] => {
  return {
    // Memory
    forgets_recent_events: formData.forgetsRecentEvents,
    repeats_questions: formData.repeatsQuestions,
    // Executive Function
    difficulty_planning: formData.difficultyPlanning,
    difficulty_managing_finances: formData.difficultyManagingFinances,
    // Language
    difficulty_finding_words: formData.difficultyFindingWords,
    difficulty_understanding_speech: formData.difficultyUnderstandingSpeech,
    // Visuospatial
    gets_lost_familiar_places: formData.getsLostFamiliarPlaces,
    difficulty_recognizing_faces: formData.difficultyRecognizingFaces,
    // Perception & Sleep Behavior
    visual_hallucinations: formData.visualHallucinations,
    acting_out_dreams: formData.actingOutDreams,
    // Motor Symptoms
    slowness_or_stiffness: formData.slownessOrStiffness,
    slow_movement_or_balance_issues: formData.slowMovementOrBalanceIssues,
  };
};

const mapFamilyRelation = (
  relation?: string,
): "PARENT" | "SIBLING" | "OTHER" => {
  switch (relation?.toUpperCase()) {
    case "PARENT":
      return "PARENT";
    case "SIBLING":
      return "SIBLING";
    default:
      return "OTHER";
  }
};

/**
 * Map all form data to CaregiverQuestionnaireDTO
 */
export const mapAllFormDataToCaregiverQuestionnaireDTO = (
  neurological: NeurologicalHistoryFormData,
  behavioral: BehavioralFormData,
  cognitive: CognitiveFunctionalFormData,
): CaregiverQuestionnaireDTO => {
  const normalize = (value?: string) =>
    value?.toLowerCase().replace(/\s+/g, "-") || "";

  const mapSleep = (value?: string) => {
    if (!value?.trim()) return null;
    switch (normalize(value)) {
      case "excellent":
        return "EXCELLENT";
      case "good":
        return "GOOD";
      case "poor":
        return "POOR";
      default:
        return null;
    }
  };

  const mapMood = (value?: string) => {
    if (!value?.trim()) return null;
    switch (normalize(value)) {
      case "not-at-all":
        return "NOT_AT_ALL_DEPRESSED";
      case "several-days":
        return "SEVERAL_DAYS";
      case "nearly-every-day":
        return "NEARLY_EVERY_DAY";
      default:
        return null;
    }
  };

  const mapAnxiety = (value?: string) => {
    if (!value?.trim()) return null;
    switch (normalize(value)) {
      case "not-anxious":
        return "NOT_ANXIOUS";
      case "moderately-anxious":
        return "MODERATELY_ANXIOUS";
      case "very-anxious":
        return "VERY_ANXIOUS";
      default:
        return null;
    }
  };

  const mapApathy = (value?: string) => {
    if (!value?.trim()) return null;
    switch (normalize(value)) {
      case "no-apathy":
        return "NO_APATHY";
      case "slight-apathy":
        return "SLIGHT_APATHY";
      case "mild-apathy":
        return "MILD_APATHY";
      case "moderate-apathy":
        return "MODERATE_APATHY";
      case "severe-apathy":
        return "SEVERE_APATHY";
      default:
        return null;
    }
  };

  const hasFamilyHistory = neurological.hasFamilyNeurodegenerative ?? false;

  return {
    neurological_medical_history: {
      prior_diagnosis: {
        has_diagnosis: neurological.hasNeurologicalDiagnosis ?? false,
        condition: neurological.neurologicalCondition || "",
        year_of_diagnosis: neurological.neurologicalDiagnosisYear || "",
      },

      stroke_history: {
        history_of_stroke: neurological.hasHadStroke ?? false,
        stroke_years: neurological.strokeYear || "",
        history_of_tia: neurological.hasHadTIA ?? false,
        tia_years: neurological.tiaYear || "",
        multiple_strokes: neurological.hasMultipleStrokes ?? false,
        multiple_strokes_years: neurological.multipleStrokesYear || "",
      },

      head_injury: {
        significant_head_injury: neurological.hasHeadInjury ?? false,
        loss_of_consciousness:
          neurological.headInjuryLossOfConsciousness ?? false,
        year: neurological.headInjuryYear || "",
      },

      cardiovascular_risk_factors: {
        hypertension: neurological.hasHypertension ?? false,
        diabetes: neurological.hasDiabetes ?? false,
        high_cholesterol: neurological.hasHighCholesterol ?? false,
        heart_disease: neurological.hasHeartDisease ?? false,
      },

      family_history: {
        has_family_history: hasFamilyHistory,
        relation: hasFamilyHistory
          ? mapFamilyRelation(neurological.familyRelation)
          : "PARENT",
        other_relation:
          hasFamilyHistory &&
          mapFamilyRelation(neurological.familyRelation) === "OTHER"
            ? neurological.familyOtherRelation || ""
            : "",
      },

      onset_of_symptoms: {
        gradual_progression: neurological.onsetPattern === "gradual",
        sudden_onset: neurological.onsetPattern === "sudden",
        step_wise_decline: neurological.onsetPattern === "stepwise",
      },
    },

    behavioral_psychological_changes: {
      sleep_past_7_days: mapSleep(behavioral.sleepQuality) || "EXCELLENT",
      mood_past_2_weeks:
        mapMood(behavioral.depressionFrequency) || "NOT_AT_ALL_DEPRESSED",
      anxiety_current: mapAnxiety(behavioral.anxietyLevel) || "NOT_ANXIOUS",
      interest_in_activities_past_week:
        mapApathy(behavioral.apathyLevel) || "NO_APATHY",
    },

    cognitive_functional_indicators: {
      memory: {
        forgets_recent_events: cognitive.forgetsRecentEvents ?? false,
        repeats_questions: cognitive.repeatsQuestions ?? false,
      },

      executive_function: {
        difficulty_planning_tasks: cognitive.difficultyPlanning ?? false,
        difficulty_managing_finances:
          cognitive.difficultyManagingFinances ?? false,
      },

      language: {
        difficulty_finding_words: cognitive.difficultyFindingWords ?? false,
        difficulty_understanding_speech:
          cognitive.difficultyUnderstandingSpeech ?? false,
      },

      visuospatial: {
        gets_lost_in_familiar_places:
          cognitive.getsLostFamiliarPlaces ?? false,
        difficulty_recognizing_faces_or_objects:
          cognitive.difficultyRecognizingFaces ?? false,
      },

      perception_sleep: {
        visual_hallucinations: cognitive.visualHallucinations ?? false,
        acts_out_dreams: cognitive.actingOutDreams ?? false,
      },

      motor_symptoms: {
        tremors_or_stiffness: cognitive.slownessOrStiffness ?? false,
        slowed_movement_or_balance_issues:
          cognitive.slowMovementOrBalanceIssues ?? false,
      },
    },
  };
};

export const mapCaregiverQuestionnaireRequestBody = (
  neurological: NeurologicalHistoryFormData,
  behavioral: BehavioralFormData,
  cognitive: CognitiveFunctionalFormData,
  skippedSteps: CaregiverQuestionnaireSkippedSteps = {},
): Record<string, unknown> => {
  const body: Record<string, unknown> = {
    ...mapAllFormDataToCaregiverQuestionnaireDTO(
      neurological,
      behavioral,
      cognitive,
    ),
  };

  if (skippedSteps.neurological) {
    body.neurological_medical_history = skippedNeurologicalMedicalHistory();
  }

  if (skippedSteps.behavioral) {
    body.behavioral_psychological_changes =
      skippedBehavioralPsychologicalChanges();
  }

  if (skippedSteps.cognitiveFunctional) {
    body.cognitive_functional_indicators =
      skippedCognitiveFunctionalIndicators();
  }

  return body;
};

export const mapPatientAssessmentDetail = (
  dto: PatientAssessmentDetailDTO,
): PatientAssessmentDetail => {
  const { assessment, accessibility_sensory, caregiver_questionnaire } =
    dto.data;

  return {
    id: assessment.id,
    stage: assessment.stage,
    status: assessment.status,

    accessibility: accessibility_sensory
      ? {
          vision: accessibility_sensory.vision.severity,
          hearing: accessibility_sensory.hearing.severity,
          speech: accessibility_sensory.speech.severity,
        }
      : undefined,

    caregiver: caregiver_questionnaire
      ? {
          sleep:
            caregiver_questionnaire.behavioral_psychological_changes
              .sleep_past_7_days,
          mood: caregiver_questionnaire.behavioral_psychological_changes
            .mood_past_2_weeks,
        }
      : undefined,
  };
};
